import React from 'react';
import { Info, MapPin, Calendar, TrendingUp, Compass, SunSnow, Train, Shield, ShoppingBag, AlertTriangle } from 'lucide-react';

/**
 * Компонент с информационным контентом о путешествиях по России
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-purple-500" />
        Всё о путешествиях по России: советы, сезоны и особенности
      </h2>
      
      <div className="prose prose-purple dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <MapPin className="w-5 h-5 text-purple-500" />
          Популярные направления
        </h3>
        <p>
          Россия — огромная страна с множеством уникальных направлений для путешествий. <strong>Москва</strong> и <strong>Санкт-Петербург</strong> остаются самыми посещаемыми городами благодаря богатому культурному наследию, музеям мирового класса и архитектуре. Для ценителей природы <strong>Байкал</strong>, <strong>Алтай</strong>, <strong>Карелия</strong> и <strong>Камчатка</strong> предлагают захватывающие пейзажи и активный отдых.
        </p>
        <p>
          <strong>Золотое кольцо</strong> — популярный туристический маршрут, включающий древние города России с богатым культурным и историческим наследием. А <strong>Сочи</strong> и побережье <strong>Черного моря</strong> привлекают любителей пляжного отдыха и горнолыжников зимой.
        </p>

        <h3 className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-purple-500" />
          Лучшее время для поездок
        </h3>
        <p>
          Лучшее время для путешествия зависит от региона и ваших интересов:
        </p>
        <ul>
          <li><strong>Весна (май-июнь):</strong> Комфортная погода в большинстве регионов, цветение, меньше туристов.</li>
          <li><strong>Лето (июнь-август):</strong> Пик туристического сезона, идеально для северных регионов, но цены выше.</li>
          <li><strong>Осень (сентябрь-октябрь):</strong> Золотая осень с красивыми пейзажами, комфортная температура.</li>
          <li><strong>Зима (декабрь-февраль):</strong> Отличное время для зимних активностей, горнолыжных курортов и аутентичного опыта русской зимы.</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <SunSnow className="w-5 h-5 text-purple-500" />
          Сезонность и климат
        </h3>
        <p>
          Учитывайте, что Россия имеет несколько климатических зон:
        </p>
        <ul>
          <li><strong>Европейская часть:</strong> Умеренный климат с теплым летом (до +30°C) и холодной зимой (до -20°C).</li>
          <li><strong>Сибирь:</strong> Континентальный климат с коротким теплым летом и суровой зимой (до -40°C).</li>
          <li><strong>Юг России:</strong> Субтропический климат на Черноморском побережье с жарким летом и мягкой зимой.</li>
          <li><strong>Дальний Восток:</strong> Муссонный климат с влажным летом и холодной ветреной зимой.</li>
        </ul>
        <p>
          В высокий сезон (лето для большинства направлений) цены могут вырасти на 30-50%, а достопримечательности будут переполнены туристами. Низкий сезон предлагает значительные скидки, но некоторые активности могут быть недоступны.
        </p>

        <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-100 dark:border-purple-800 my-6">
          <h4 className="text-purple-700 dark:text-purple-300 font-medium">Как сэкономить на жилье?</h4>
          <p className="text-purple-600 dark:text-purple-400 text-sm mb-0">
            Планируйте поездку в низкий или межсезонье, бронируйте заранее (за 2-3 месяца), рассматривайте варианты за пределами центра города. Для длительного пребывания апартаменты обычно выгоднее отелей благодаря возможности готовить.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-purple-500" />
          Расходы и бюджет путешествия
        </h3>
        <p>
          Стоимость путешествия по России зависит от выбранных направлений, сезона и стиля поездки:
        </p>
        <ul>
          <li><strong>Бюджетная поездка:</strong> 2000-3500 ₽ на человека в день (хостелы, общественный транспорт, недорогие кафе).</li>
          <li><strong>Средний уровень:</strong> 4000-8000 ₽ на человека в день (3-4 звездочные отели, рестораны, экскурсии).</li>
          <li><strong>Высокий уровень:</strong> От 10000 ₽ на человека в день (роскошные отели, частный транспорт, гастрономические рестораны).</li>
        </ul>
        <p>
          Москва и Санкт-Петербург традиционно дороже других городов на 20-30%. Курортные направления вроде Сочи имеют высокие цены в пиковый сезон. В небольших городах и региональных центрах расходы обычно ниже.
        </p>

        <h3 className="flex items-center gap-2">
          <Train className="w-5 h-5 text-purple-500" />
          Транспорт по России
        </h3>
        <p>
          Россия располагает обширной транспортной сетью:
        </p>
        <ul>
          <li><strong>Авиасообщение:</strong> Самый быстрый способ преодолевать большие расстояния. Основные авиахабы — Москва, Санкт-Петербург, Екатеринбург, Новосибирск.</li>
          <li><strong>Железнодорожное сообщение:</strong> Обширная сеть соединяет большинство крупных городов. Фирменные поезда предлагают высокий уровень комфорта.</li>
          <li><strong>Автобусы:</strong> Экономичный вариант для путешествий между близлежащими городами.</li>
          <li><strong>Аренда автомобиля:</strong> Предоставляет гибкость, но требует подготовки к российским дорогам и правилам.</li>
        </ul>
        <p>
          <strong>Совет:</strong> Для дальних расстояний советуем использовать самолет или поезд. Знаменитая Транссибирская магистраль — это не только способ передвижения, но и уникальный опыт путешествия через всю страну.
        </p>

        <h3 className="flex items-center gap-2">
          <Compass className="w-5 h-5 text-purple-500" />
          Особенности путешествий по России
        </h3>
        <ul>
          <li><strong>Языковой барьер:</strong> За пределами крупных городов и туристических зон английский менее распространен. Полезно знать базовые фразы на русском языке или использовать приложения-переводчики.</li>
          <li><strong>Расстояния:</strong> Россия — самая большая страна в мире. Будьте готовы к длительным переездам между регионами.</li>
          <li><strong>Интернет и связь:</strong> Мобильная связь и 4G интернет доступны в большинстве населенных пунктов. Рекомендуется приобретать местные SIM-карты для экономии.</li>
          <li><strong>Безопасность:</strong> Большинство туристических регионов России безопасны для путешественников. Соблюдайте стандартные меры предосторожности, как в любом другом месте.</li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Полезные мелочи для путешествия по России
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>В российских городах часто бывают значительные перепады температур даже в течение дня. Одевайтесь слоями.</li>
            <li>Во многих музеях и храмах требуют снимать обувь или надевать бахилы. Имейте при себе удобную обувь и носки.</li>
            <li>В некоторых храмах и монастырях женщинам необходимо покрывать голову и плечи.</li>
            <li>Большинство банкоматов и торговых точек принимают международные карты, но иметь при себе наличные рубли всегда полезно.</li>
            <li>В зимнее время рассчитывайте на короткий световой день (в Санкт-Петербурге в декабре — около 5.5 часов света).</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-green-500" />
          Документы и подготовка
        </h3>
        <p>
          Для успешного путешествия по России рекомендуется:
        </p>
        <ul>
          <li><strong>Подготовить документы:</strong> Паспорт, виза (если требуется), страховой полис, бронь отеля.</li>
          <li><strong>Проверить погоду:</strong> Российский климат переменчив, особенно в межсезонье. Проверяйте прогноз перед поездкой.</li>
          <li><strong>Приобрести страховку:</strong> Медицинская страховка обязательна для комфортного путешествия, особенно при активном отдыхе.</li>
          <li><strong>Составить маршрут:</strong> Из-за больших расстояний важно планировать перемещения заранее.</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <ShoppingBag className="w-5 h-5 text-purple-500" />
          Сувениры и шоппинг
        </h3>
        <p>
          Популярные сувениры из России включают:
        </p>
        <ul>
          <li><strong>Матрешки и изделия из дерева:</strong> Классический русский сувенир, доступный в разных ценовых категориях.</li>
          <li><strong>Павловопосадские платки:</strong> Традиционные шерстяные платки с ярким цветочным орнаментом.</li>
          <li><strong>Янтарь:</strong> Особенно популярен в Калининградской области.</li>
          <li><strong>Шкатулки с палехской, федоскинской миниатюрой:</strong> Предметы традиционного русского искусства.</li>
          <li><strong>Русская косметика и продукты:</strong> Локальные бренды часто предлагают качественные товары по доступным ценам.</li>
        </ul>

        <p className="font-semibold mt-6">
          Россия — страна удивительного разнообразия, где каждый регион предлагает уникальные впечатления. Наш калькулятор поможет вам спланировать бюджет поездки и сделать ваше путешествие незабываемым!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;